import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Input } from '../../../../../components/ui/input';
import { AntdDate } from '../../../../../components/ui/antd-date';
import { Label } from '../../../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import { cn } from '../../../../../lib/utils';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { Notification } from '../../../../../components/ui/custom-notification';
import * as spoolsbaseActions from '../../../../../redux/Spoolsbase/actions';
import type { DashboardProject } from '../../../types/project';
import type { SignaturePanelProps } from './components/weld/signatureTypes';
import { buildQcSignaturePayload } from './components/weld/spoolStepPayloadHelpers';
import { Signature } from './Signature';
import type { NcrRow, SpoolTrackerWeldStepType, SpoolWorkflowNcrRegisterPayload } from './spoolNcrApi';

export interface RaiseWorkflowNCRModalProps {
  open: boolean;
  onClose: () => void;
  project?: DashboardProject;
  projectId: string | number | null;
  pipeId: string | number | null;
  spoolId: number;
  /** Spool name (shown in header and payload). */
  spoolName: string;
  /** API weld step id (e.g. fitup, vt). */
  weldStepType: SpoolTrackerWeldStepType;
  /** Workflow step label, e.g. Fit-Up, Preheat, NDT. */
  categoryLabel: string;
  /** Weld joint id (when a joint is in context). */
  weldId?: string | null;
  /** Optional weld number for context line / payload. */
  weldRef?: string | null;
  sig: SignaturePanelProps;
  /** When set, form opens with this NCR; saves update that row (uses `id` in API body). */
  prefillListRow: NcrRow | null;
}

function draftNcrNo() {
  const d = new Date();
  const y = d.getFullYear();
  const n = Math.floor(Math.random() * 900) + 100;
  return `NCR-${y}-${n}`;
}

type Disposition = 'pending' | 'rework' | 'accept' | 'reject';
type Buyer = 'no' | 'yes' | 'yes_date';
type NcrStatus = 'open' | 'pending_closure' | 'closed';

export const RaiseWorkflowNCRModal: React.FC<RaiseWorkflowNCRModalProps> = ({
  open,
  onClose,
  project,
  projectId,
  pipeId,
  spoolId,
  spoolName,
  weldStepType,
  categoryLabel,
  weldId = null,
  weldRef,
  sig,
  prefillListRow = null,
}) => {
  const [ncrNo, setNcrNo] = useState(() => draftNcrNo());
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [ncrDate, setNcrDate] = useState(today);
  const [description, setDescription] = useState('');
  const [details, setDetails] = useState('');
  const [disposition, setDisposition] = useState<Disposition>('pending');
  const [buyerNotified, setBuyerNotified] = useState<Buyer>('no');
  const [ncrStatus, setNcrStatus] = useState<NcrStatus>('open');
  const dispatch = useDispatch();
  const ncrSaving = useSelector((s: { spoolsbase?: { savingStep?: string | null } }) => s.spoolsbase?.savingStep === 'ncr');
  const lastSaved = useSelector(
    (s: {
      spoolsbase?: {
        lastSaved?: {
          step: string;
          at: number;
          request?: SpoolWorkflowNcrRegisterPayload | null;
        } | null;
      };
    }) => s.spoolsbase?.lastSaved ?? null
  );
  const openStartedAtRef = useRef(0);
  /** Submit NCR allowed only after a successful Save Draft in this session (editing existing row counts as draft already saved). */
  const [draftSavedThisSession, setDraftSavedThisSession] = useState(() => Boolean(prefillListRow));
  const [pendingSaveMode, setPendingSaveMode] = useState<'draft' | 'submit' | null>(null);
  const isSavingDraft = ncrSaving && pendingSaveMode === 'draft';

  /** Until a draft exists, ignore shared modal QC signature state so Fit-Up / other steps don’t leak into this form. */
  const sigForNcrPanel = useMemo(
    (): SignaturePanelProps =>
      draftSavedThisSession ? sig : { ...sig, qcSignatureData: null },
    [draftSavedThisSession, sig]
  );

  const qcSignaturePayload = useMemo(() => buildQcSignaturePayload(sigForNcrPanel), [sigForNcrPanel]);
  const hasQcSignature = qcSignaturePayload.signed;

  const canSubmitNcr = draftSavedThisSession && hasQcSignature && !ncrSaving;

  useEffect(() => {
    if (!open) return;
    if (prefillListRow) {
      setNcrNo(prefillListRow.ncrNo);
      setNcrDate((prefillListRow.ncrDateRaw || new Date().toISOString().slice(0, 10)).slice(0, 10));
      setDescription(prefillListRow.description);
      const detailBlock = [prefillListRow.rootCause, prefillListRow.correctiveAction, prefillListRow.detailsText]
        .map((s) => String(s || '').trim())
        .filter(Boolean);
      setDetails(
        detailBlock.length > 0
          ? detailBlock.join('\n\n')
          : (prefillListRow.detailsText || '').trim()
      );
      const dsel = prefillListRow.dispositionSelect;
      if (dsel === 'accept' || dsel === 'rework' || dsel === 'reject' || dsel === 'pending' || dsel === 'repair') {
        if (dsel === 'repair') {
          setDisposition('rework');
        } else {
          setDisposition(dsel);
        }
      } else {
        setDisposition('pending');
      }
      const b = (prefillListRow.buyerNotified || 'no').toLowerCase();
      if (b.includes('date')) setBuyerNotified('yes_date');
      else if (b === 'yes' || b.includes('yes')) setBuyerNotified('yes');
      else setBuyerNotified('no');
      const st = prefillListRow.status;
      if (st === 'Closed') setNcrStatus('closed');
      else if (st === 'Pending Closure') setNcrStatus('pending_closure');
      else setNcrStatus('open');
    } else {
      setNcrNo(draftNcrNo());
      setNcrDate(new Date().toISOString().slice(0, 10));
      setDescription('');
      setDetails('');
      setDisposition('pending');
      setBuyerNotified('no');
      setNcrStatus('open');
    }
  }, [open, prefillListRow, categoryLabel, spoolName, weldStepType, spoolId]);

  useEffect(() => {
    if (open) {
      openStartedAtRef.current = Date.now();
      setDraftSavedThisSession(Boolean(prefillListRow));
      setPendingSaveMode(null);
    }
  }, [open, prefillListRow]);

  useEffect(() => {
    if (!ncrSaving) setPendingSaveMode(null);
  }, [ncrSaving]);

  /** After successful draft save in this session, allow final submit (still requires QC signature). */
  useEffect(() => {
    if (!open || !lastSaved || lastSaved.step !== 'ncr') return;
    if (lastSaved.at < openStartedAtRef.current) return;
    if (lastSaved.request?.submissionMode === 'draft') {
      setDraftSavedThisSession(true);
    }
  }, [open, lastSaved]);

  /** Close only after final submit — not after Save Draft (user must sign then submit). */
  useEffect(() => {
    if (!open || !lastSaved || lastSaved.step !== 'ncr') return;
    if (lastSaved.at < openStartedAtRef.current) return;
    if (lastSaved.request?.submissionMode === 'submit') onClose();
  }, [open, lastSaved, onClose]);

  const buildAndSubmit = (submissionMode: 'draft' | 'submit') => {
    if (projectId == null || projectId === '' || pipeId == null || pipeId === '') {
      Notification.error('NCR', 'Project and pipe are required to register an NCR.');
      return;
    }
    if (submissionMode === 'submit' && !description.trim()) {
      Notification.error('NCR', 'Description is required to submit the NCR.');
      return;
    }
    const payload: SpoolWorkflowNcrRegisterPayload = {
      projectId,
      pipeId,
      spoolId,
      spoolName,
      weldId: weldId ?? null,
      weldNumber: weldRef != null && weldRef !== '' ? weldRef : null,
      weldStepType,
      categoryLabel,
      ncrNumber: ncrNo,
      ncrDate,
      description: description.trim(),
      disposition,
      buyerNotified,
      status: ncrStatus,
      details: details.trim(),
      signature: buildQcSignaturePayload(sigForNcrPanel),
      submissionMode,
    };
    if (prefillListRow) {
      const n = parseInt(String(prefillListRow.id), 10);
      if (Number.isInteger(n) && n > 0) {
        (payload as SpoolWorkflowNcrRegisterPayload & { id?: number; ncrId?: number }).id = n;
        (payload as SpoolWorkflowNcrRegisterPayload & { ncrId?: number }).ncrId = n;
      }
    }
    setPendingSaveMode(submissionMode);
    dispatch(spoolsbaseActions.registerSpoolNcr(payload));
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="m-0 h-screen max-h-none w-screen max-w-none gap-0 overflow-hidden rounded-none border-0 bg-white p-0 shadow-none [&>button]:hidden">
        <div className="flex h-screen flex-col">
          <DialogHeader className="flex shrink-0 flex-row items-start justify-between gap-4 border-b bg-white px-4 py-4 sm:px-6">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={onClose}
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </Button>
              <div className="min-w-0">
                <DialogTitle className="text-left text-lg font-bold text-gray-900">
                  {prefillListRow ? 'NCR' : 'Raise NCR'}
                  {prefillListRow ? <span className="ml-2 text-sm font-normal text-gray-500">(view / edit)</span> : null}
                </DialogTitle>
                {project?.project?.projectName && (
                  <p className="mt-1 truncate text-sm text-gray-500">{project.project.projectName}</p>
                )}
                <p className="mt-1 flex flex-wrap items-baseline gap-x-1 gap-y-1 text-sm text-gray-600">
                  <span className="text-gray-500">Spool</span>{' '}
                  <span className="font-semibold text-gray-900">{spoolName}</span>
                  <span className="text-gray-300">·</span>
                  <span className="text-gray-500">Category</span>{' '}
                  <span className="font-semibold text-gray-900">{categoryLabel}</span>
                  {weldRef ? (
                    <>
                      <span className="text-gray-300">·</span>
                      <span className="text-gray-500">Weld</span>{' '}
                      <span className="font-mono font-semibold text-gray-900">{weldRef}</span>
                    </>
                  ) : null}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-6 sm:px-6">
            <div className="mx-auto max-w-full space-y-5">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-700">
                    NCR # <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    className="h-9 font-mono text-sm"
                    value={ncrNo}
                    onChange={(e) => setNcrNo(e.target.value)}
                    placeholder="e.g. NCR-2026-001"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-700">
                    Date <span className="text-red-500">*</span>
                  </Label>
                  <AntdDate className="h-9 text-sm" value={ncrDate} onChange={setNcrDate} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-700">Disposition</Label>
                  <Select value={disposition} onValueChange={(v) => setDisposition(v as Disposition)}>
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="rework">Rework</SelectItem>
                      <SelectItem value="accept">Accept As-Is</SelectItem>
                      <SelectItem value="reject">Reject</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-gray-700">BUYER Notified</Label>
                  <Select value={buyerNotified} onValueChange={(v) => setBuyerNotified(v as Buyer)}>
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="yes_date">Yes (with date)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs text-gray-700">Status</Label>
                  <Select value={ncrStatus} onValueChange={(v) => setNcrStatus(v as NcrStatus)}>
                    <SelectTrigger className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="pending_closure">Pending Closure</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

          
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-700">
                  Description <span className="text-red-500">*</span>
                </Label>
                <textarea
                  className={cn(
                    'min-h-[88px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                  placeholder="Describe the non-conformance…"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-700">Root Cause / Details</Label>
                <textarea
                  className={cn(
                    'min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                  )}
                  placeholder="Root cause, references, photos, or other supporting detail…"
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                />
              </div>

              <div className="border-t pt-5">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">QC Inspector</p>
                <Signature
                  customerSignature={null}
                  qcSignature={sig.selectedQcId}
                  customerRepresentativeList={[]}
                  qcList={sig.qcList}
                  handleQcSignatureChange={sig.onQcChange}
                  signatureData={sigForNcrPanel.qcSignatureData}
                  onSignatureChange={sig.onQcSignatureChange}
                  showCustomerRep={false}
                  showQc
                  qcLabel="QC Inspector signature"
                  canSign={draftSavedThisSession}
                  canEdit
                  isEditMode
                  isAdminOrSuperAdmin
                  align="horizontal"
                  placeholder={draftSavedThisSession ? 'QC sign here' : 'Save draft first, then sign'}
                />
              </div>

              <div className="space-y-2 border-t pt-4">
                {(!draftSavedThisSession || !hasQcSignature) && (
                  <p className="text-right text-xs text-gray-500">
                    {!draftSavedThisSession && !hasQcSignature
                      ? 'Save draft first, then add a QC signature to enable Submit NCR.'
                      : !draftSavedThisSession
                        ? 'Save draft at least once to enable Submit NCR.'
                        : 'QC signature is required before you can submit the NCR.'}
                  </p>
                )}
                <div className="flex flex-wrap justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={ncrSaving}
                    onClick={() => buildAndSubmit('draft')}
                    className="min-w-[7.5rem]"
                  >
                    {isSavingDraft ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" aria-hidden />
                        Saving…
                      </>
                    ) : (
                      'Save Draft'
                    )}
                  </Button>
                  <Button
                    type="button"
                    className="bg-[#e04530] text-white hover:bg-[#e04530]/90"
                    disabled={ncrSaving || !canSubmitNcr || !description.trim()}
                    title={
                      !draftSavedThisSession
                        ? 'Save draft first'
                        : !hasQcSignature
                          ? 'QC signature required'
                          : !description.trim()
                            ? 'Description is required'
                            : undefined
                    }
                    onClick={() => buildAndSubmit('submit')}
                  >
                    Submit NCR
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
